import { z } from 'zod';

// Policy contracts. No draft, preview, saved or approved revision authorizes endpoint work.
export const windowSchema = z.object({
  timeZone: z.string().min(1).max(80).refine(s => { try { new Intl.DateTimeFormat('en', { timeZone: s }); return true; } catch { return false; } }),
  days: z.array(z.number().int().min(0).max(6)).min(1).max(7).refine(ds => new Set(ds).size === ds.length),
  startMinute: z.number().int().min(0).max(1439), durationMinutes: z.number().int().min(30).max(720),
}).strict();
export const settingsSchema = z.object({
  patchApproval: z.enum(['manual', 'security', 'security-quality']),
  maintenanceWindow: windowSchema,
  reboot: z.enum(['never', 'when-idle-in-window']),
  excluded: z.boolean(),
  defenderRealTime: z.boolean(), defenderCloud: z.boolean(), defenderPua: z.boolean(),
  smartScreen: z.boolean(), networkProtection: z.boolean(), edgeProtection: z.boolean(), chromeProtection: z.boolean(),
  preventBrowserBypass: z.boolean(),
}).strict();
export type PolicySettings = z.infer<typeof settingsSchema>;
export type SettingKey = keyof PolicySettings;
export const settingKeys = settingsSchema.keyof().options;
export const policyTargetSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('customer'), organizationId: z.uuid() }).strict(),
  z.object({ kind: z.literal('site'), organizationId: z.uuid(), siteId: z.uuid() }).strict(),
  z.object({ kind: z.literal('device'), organizationId: z.uuid(), siteId: z.uuid(), deviceId: z.uuid() }).strict(),
]);
export type PolicyTarget = z.infer<typeof policyTargetSchema>;
export const draftChangeSchema = z.object({
  target: policyTargetSchema, expectedRevision: z.number().int().min(0).max(2_147_483_646),
  settings: settingsSchema.partial(), locks: z.array(settingsSchema.keyof()).max(settingKeys.length).refine(ks => new Set(ks).size === ks.length),
  reason: z.string().trim().min(1).max(500).regex(/^[^\p{Cc}\p{Cf}]+$/u),
  expiresAt: z.iso.datetime({ offset: true }).nullable(),
}).strict().superRefine((v, ctx) => {
  if (v.target.kind !== 'device' && v.expiresAt !== null) ctx.addIssue({ code: 'custom', path: ['expiresAt'], message: 'Only device exceptions expire' });
});
export type DraftChange = z.infer<typeof draftChangeSchema>;
export interface PolicyDraft {
  target: PolicyTarget; revision: number; settings: Partial<PolicySettings>; locks: SettingKey[]; reason: string;
  expiresAt: string | null; updatedAt: string; updatedBy: string;
}
export interface PolicyOrigin { kind: 'default' | PolicyTarget['kind']; id: string | null; revision: number }
export interface ResolvedDraft {
  settings: PolicySettings; origins: Record<SettingKey, PolicyOrigin>; lockedBy: Partial<Record<SettingKey, PolicyOrigin>>;
  conflicts: Array<{ field: SettingKey; attempted: PolicyOrigin; kept: PolicyOrigin }>;
  expired: PolicyOrigin[];
}
export interface PolicyDraftContext {
  draft: PolicyDraft | null; target: PolicyTarget; approved: PolicyApproval | null;
  /** The target's saved and approved hierarchy, separately; absent on older API releases. */
  resolved?: { draft: ResolvedDraft; approved: ResolvedDraft };
  /** Stable customer policy dependency version for withdrawing previews; never approval proof or authority. */
  policyVersion?: string;
}
export interface PolicyPreviewDevice {
  id: string; hostname: string; siteId: string; os: string | null; managed: boolean;
  before: ResolvedDraft; after: ResolvedDraft; changed: SettingKey[];
  endpointState: 'not_applied';
}
export interface PolicyPreview {
  state: 'draft'; evaluatedAt: string; expiresAt: string; digest: string; target: PolicyTarget; scope: ResolvedDraft; devices: PolicyPreviewDevice[];
  counts: { total: number; changed: number; excluded: number; conflicts: number; unsupported: number };
}

// Approved policy (WRMM-144 slice 2). An approval makes one saved draft revision the desired policy for its target.
// It is not enforcement: nothing is sent to devices, and observed state comes only from existing read-only inventory.
const proofSchema = z.object({ digest: z.string().regex(/^[a-f0-9]{64}$/), expiresAt: z.iso.datetime({ offset: true }) }).strict();
export const approvalPreviewSchema = z.object({ target: policyTargetSchema, draftRevision: z.number().int().min(1).max(2_147_483_647) }).strict();
export const approvalRequestSchema = approvalPreviewSchema.extend({ preview: proofSchema }).strict();
export interface PolicyApproval {
  target: PolicyTarget; revision: number; settings: Partial<PolicySettings>; locks: SettingKey[]; reason: string;
  expiresAt: string | null; approvedAt: string; approvedBy: string;
}
export interface PolicyApprovalPreview extends Omit<PolicyPreview, 'state' | 'devices'> {
  state: 'approval'; draftRevision: number; devices: Array<Omit<PolicyPreviewDevice, 'endpointState'> & { endpointState: 'not_enforced' }>;
}
export type ObservedState = 'matches' | 'differs' | 'stale' | 'unknown' | 'not_reported' | 'unsupported';
export interface PolicyObservation { state: ObservedState; observed: boolean | null; observedAt: string | null; detail: string | null }
export interface EffectivePolicy {
  target: Extract<PolicyTarget, { kind: 'device' }>; evaluatedAt: string;
  /** Resolved from approved revisions only; origins name the approved draft revision of each layer. */
  desired: ResolvedDraft; approvals: PolicyApproval[];
  /** Settings whose saved drafts resolve differently from what is approved. */
  draftDiffers: SettingKey[];
  observed: Record<SettingKey, PolicyObservation>; endpointState: 'not_enforced';
}
